import { useState } from "react";
import { Occurrence } from "../../../models/occurrence";
import SummaryDialog from "../dialogs/summaryDialog";
import {
  convertTimeStringToMilliseconds,
  formatMilisecondsInDaysHoursMinutes,
} from "../../../utils/dateUtils";
import { OccurrenceStepEnum } from "../../../enums/occurrenceStep";
import { SimpleTooltip } from "@rio-cloud/rio-uikit";
import { useMediaQuery } from "@mui/material";
import { useCurrentStep } from "../../../hooks/useGetCurrentStep";
import { OccurrenceTypeLabelEnum } from "../../../enums/occurrenceType";
import VehicleIcon from "../../VehicleIcon";

interface IVehicleCardProps {
  occurrence: Occurrence;
  index: number;
  isValidStep: (requiredFields: string[]) => boolean;
  loading: boolean;
  save: () => Promise<boolean>;
  disabledAllFields?: boolean;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  occurrenceStepAvailable: string[];
  partners?: { id: string; name: string }[];
  changeStep?: (
    occurrence: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null>;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
}

export const VehicleCard = (props: IVehicleCardProps) => {
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const currentStep = useCurrentStep(props.occurrence.steps ?? []);
  const steps = props.occurrence.steps ?? [];
  const hasNoSteps = steps.length === 0;
  const isFinalized = Boolean(props.finalized);
  const statusLabel = isFinalized
    ? "Concluído"
    : currentStep?.isInProgress
    ? currentStep.label
    : hasNoSteps
    ? "Não iniciado"
    : currentStep?.label || "";

  const criticalityColorMap = {
    5: "bg-red-500",
    4: "bg-orange-400",
    3: "bg-yellow-500",
    2: "bg-green-500",
    1: "bg-blue-500",
  };

  const criticalityLabelMap = {
    5: "Emergência",
    4: "Muito urgente",
    3: "Urgente",
    2: "Pouco urgente",
    1: "Não urgente",
  };

  const getAssetModel = (occurrence: Occurrence) => {
    if(occurrence.vehicleName && (occurrence.vehicleName !== '-')) return occurrence.vehicleName;
    return occurrence.vehicleModel;
  }

  return (
    <>
      <div className="w-[300px]">
        <div
          onClick={() => setShowSummaryDialog(true)}
          className="p-[10px] cursor-pointer"
          style={{
            backgroundColor: "#C7D1D9",
            borderBottom: "16px solid #FFFFFF",
          }}
        >
          <div
            key={props.index}
            className={`border border-black rounded-lg p-4 bg-white h-[225px] select-none ${
              isMobile ? "w-[300px]" : ""
            }`}
            style={{ borderWidth: "1.7px", borderColor: "black" }}
          >
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center space-x-2">
                <VehicleIcon
                  chassis={props.occurrence.vehicleChassis}
                  size={20}
                  className=""
                />
                <span className="font-semibold text-[14px] truncate max-w-[100px] select-none">
                  {getAssetModel(props.occurrence)}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <span
                  className={`w-3 h-3 rounded-full ${
                    criticalityColorMap[
                      props.occurrence
                        .criticality as unknown as keyof typeof criticalityColorMap
                    ]
                  }`}
                ></span>
                <span className="text-gray-500 text-[14px] pl-1 select-none">
                  {
                    criticalityLabelMap[
                      props.occurrence
                        .criticality as unknown as keyof typeof criticalityLabelMap
                    ]
                  }
                </span>
              </div>
            </div>

            <hr style={{ margin: 0 }} />

            {/* Dados principais */}
            <div className="py-2 space-y-2 text-[14px]">
              <div className="flex justify-between text-gray-700">
                <span className="select-none">{props.occurrence.vehicleChassis}</span>
                <span className="select-none">{props.occurrence.vehicleLicensePlate}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span className="select-none">{props.occurrence.vehicleOdometer}</span>
                <span className="select-none">{props.occurrence.vehicleHourmeter}</span>
              </div>

              <div className="flex justify-between items-center">
                <span>Tempo estimado:</span>
                <div className="flex items-center space-x-1">
                  <span className="rioglyph rioglyph-time-alt" />
                  <span className="select-none">
                    {formatMilisecondsInDaysHoursMinutes(
                      (props.occurrence.steps ?? [])
                        .map((step) =>
                          convertTimeStringToMilliseconds(
                            step.estimatedTime ?? "00:00"
                          )
                        )
                        .reduce((acc, curr) => acc + curr, 0)
                    )}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Tempo total:</span>
                <div className="flex items-center space-x-1 font-bold">
                  <span className="rioglyph rioglyph-time-alt" />
                  <span className="select-none">
                    {formatMilisecondsInDaysHoursMinutes((props.occurrence.timeSpent ?? 0) * 60000)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="truncate max-w-[120px]">
                  <SimpleTooltip
                    content={props.occurrence.accountName}
                    placement="top"
                  >
                    <span className="select-none">{props.occurrence.accountName}</span>
                  </SimpleTooltip>
                </div>
                <div className="truncate max-w-[120px] pl-4">
                  <SimpleTooltip
                    content={
                      props.occurrence.serviceBaySchedule?.serviceBay?.name
                    }
                    placement="top"
                  >
                    <span>
                      {props.occurrence.serviceBaySchedule?.serviceBay?.name}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Concessionária:</span>
                <div className="flex items-center space-x-1">
                  <SimpleTooltip
                    content={`${props.occurrence.dealershipName} - DN: ${props.occurrence.dealershipDn}`}
                    placement="top"
                  >
                    <span className="truncate max-w-[120px] select-none">
                      {props.occurrence.dealershipName} - DN: {props.occurrence.dealershipDn}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Etapa atual:</span>
                <div className="flex items-center space-x-1">
                  <SimpleTooltip
                    content={statusLabel}
                    placement="top"
                  >
                    <span className="truncate max-w-[120px] select-none">
                      {statusLabel}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              {props.occurrence.occurrenceType && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-md text-gray-600">Tipo:</span>
                 <SimpleTooltip
                    content={OccurrenceTypeLabelEnum[props.occurrence.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum]}
                    placement="top"
                  >
                    <span className="text-md font-medium select-none">
                      {OccurrenceTypeLabelEnum[props.occurrence.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum]}
                    </span>
                  </SimpleTooltip>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      <SummaryDialog
        showDialog={showSummaryDialog}
        onClose={() => setShowSummaryDialog(false)}
        occurrence={props.occurrence}
        isValidStep={props.isValidStep}
        loading={props.loading}
        save={props.save}
        disabledAllFields={props.disabledAllFields}
        persona={props.persona}
        finalized={props.finalized}
        occurrenceStepAvailable={props.occurrenceStepAvailable}
        partners={props.partners}
        changeStep={props.changeStep}
        getNotFilledRequiredFields={props.getNotFilledRequiredFields}
      />
    </>
  );
};

export const SkeletonVehicleCard = (props: { index: number }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="w-[300px]">
      <div
        className="p-[10px] cursor-pointer"
        style={{
          backgroundColor: "#C7D1D9",
          borderBottom: "16px solid #FFFFFF",
        }}
      >
        <div
          key={props.index}
          className={`border border-black rounded-lg p-4 bg-white h-[205px] ${
            isMobile ? "w-[300px]" : ""
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 mb-2">
              <div className="h-[20px] w-[20px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[20px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
            <div className="flex items-center space-x-1 mb-2">
              <div className="h-[12px] w-[12px] animate-pulse rounded-full bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
          </div>

          <hr style={{ margin: 0 }} />

          <div className="py-2 space-y-2 text-[14px] mb-2">
            <div className="flex justify-between text-gray-700">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
            <div className="flex justify-between text-gray-700 mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>

            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="flex items-center space-x-1">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="flex items-center space-x-1 font-bold">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="flex items-center space-x-1 font-bold">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
